import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, Router, TitleStrategy, withViewTransitions } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AccessGuard } from './access-guard';
import { AddInAccessGuard } from './addin-v2/addin-access-guard';
import { AuthService } from './auth.service';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { Configuration } from '../api';
import { environment } from '../environments/environment';
import { BUILD_ID } from '../environments/build-id';
import * as Sentry from '@sentry/angular';
import { UserSeatService } from './new/services/user-seat.service';
import { AdminGuard } from './admin-guard';
import { AssociateTitleStrategy } from './new/services/associate-title.strategy';
import { ChunkLoadingService } from './new/services/chunk-loading.service';
import { AppUpdateService } from './new/services/app-update.service';
import { provideZard } from './shared/core/provider/providezard';
import { demoApiInterceptor } from './new/services/demo';
import { maintenanceInterceptor } from './new/services/maintenance.interceptor';
import { actAsInterceptor } from './new/services/act-as.interceptor';

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    '/assets/i18n/',
    '.json?v=' + BUILD_ID,
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    AccessGuard,
    AdminGuard,
    AddInAccessGuard,
    AuthService,
    UserSeatService,
    provideZard(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions()),
    { provide: TitleStrategy, useClass: AssociateTitleStrategy },
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptors([actAsInterceptor, maintenanceInterceptor, demoApiInterceptor])),
    { provide: LOCALE_ID, useValue: 'de-DE' },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    {
      provide: Configuration,
      useFactory: (authService: AuthService) =>
        new Configuration({
          basePath: environment.apiUrl.replace(/\/+$/, ''),
          credentials: {
            BearerAuth: () => authService.getAccessToken(),
          },
        }),
      deps: [AuthService],
      multi: false,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    // Construct early so NavigationStart for the initial route is not missed.
    provideAppInitializer(() => {
      inject(ChunkLoadingService);
    }),
    // Watches for a new deploy and reloads stale tabs at the next navigation.
    provideAppInitializer(() => {
      inject(AppUpdateService);
    }),
  ],
};
